// apps/web/src/features/auth/hooks/useAuthBootstrap.ts
// Spec §13.3 verbatim: boot-time silent refresh. Coordinates with
// `coordinatedRefresh` single-flight mutex; uses AbortController + cancelled
// flag to survive React 18 Strict Mode's double-mount-in-dev.
import { useEffect } from 'react';
import { useApiClient } from '@magimp/shared-hooks';
import { MeResponseSchema, type MeResponse } from '@magimp/shared';
import { ZodError } from 'zod';
import { useAuthStore } from '../../../stores/auth.js';
import { coordinatedRefresh } from '../../../api/refresh.js';
import { ApiClientError, NetworkError, ThrottleError } from '../../../api/errors.js';
import { clearWithBroadcast } from '../../../api/authEvents.js';

const THROTTLE_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

// WHY: persistent /auth/me schema drift would otherwise lock the boot in an
// infinite long-tail loop (every tick re-rotates refresh, re-parses /me,
// fails the same way) and ProtectedRoute would render LoadingState forever
// — no /login escape, no error UI. After this many ZodError outcomes we
// give up and fall through to clear() so the user lands on /login. The
// drift itself is a backend bug; clear() doesn't fix it, but at least the
// user sees a usable page (and the console error makes the cause obvious).
const CONTRACT_DRIFT_RETRY_BUDGET = 5;
// WHY: matches the backend @Throttle TTL on /auth/refresh (60_000ms). After
// this delay the prior throttle window has expired and a fresh refresh
// should succeed unless the limit is being hit by another tab/process.
// Honoring the 429 Retry-After header precisely is deferred to issue #177's
// coordinator-cooldown work in coordinatedRefresh.
const LONG_TAIL_DELAY_MS_DEFAULT = 60_000;

// WHY: a 60s real-timer wait per test would make the long-tail suite
// unworkable, and fake timers don't reliably drain the fetch/MSW microtask
// chain inside the timer callback. Tests set this global to a small positive
// value (e.g. 50ms) and run the suite with real timers.
//
// SECURITY: gated behind `import.meta.env.DEV` (Vite replaces this with a
// boolean literal at build time, dead-code-eliminating the override branch
// from production bundles). Without this gate, any same-origin script on a
// production page could collapse the 60s cooldown into a refresh-flood by
// setting the global. Validated as a finite positive integer ≥ 1ms before
// use to reject NaN/Infinity/negative inputs from buggy tests.
function getLongTailDelayMs(): number {
  if (import.meta.env.DEV) {
    const override = (globalThis as { __MAGIMP_LONG_TAIL_DELAY_MS_FOR_TESTING__?: number })
      .__MAGIMP_LONG_TAIL_DELAY_MS_FOR_TESTING__;
    if (typeof override === 'number' && Number.isFinite(override) && override >= 1) {
      return override;
    }
  }
  return LONG_TAIL_DELAY_MS_DEFAULT;
}

export function useAuthBootstrap(): void {
  const client = useApiClient();
  const hydrating = useAuthStore((s) => s.hydrating);
  const setSession = useAuthStore((s) => s.setSession);
  const clear = useAuthStore((s) => s.clear);
  const setBootstrapThrottled = useAuthStore((s) => s.setBootstrapThrottled);

  useEffect(() => {
    if (!hydrating) return;

    // React 18 Strict Mode fires effects twice in dev; the AbortController
    // + cancelled flag prevents a stale setSession after unmount and
    // aborts the in-flight /auth/me if the effect re-fires.
    const ac = new AbortController();
    let cancelled = false;
    // INVARIANT: closure-scoped (not useRef) so each StrictMode mount has its
    // own timer that the cleanup can clearTimeout independently.
    let longTailTimerId: ReturnType<typeof setTimeout> | null = null;
    // Counter for /auth/me ZodError outcomes (see CONTRACT_DRIFT_RETRY_BUDGET).
    // Reset implicitly on next mount; not a useRef because we want a fresh
    // budget for every StrictMode mount cycle.
    let contractDriftCount = 0;

    // attemptBootstrap: one full pass of coordinatedRefresh + /auth/me +
    // setSession. Returns 'session' on success, 'cancelled' if the effect
    // unmounted, 'throttle' if the refresh hit 429, 'transient' for 5xx
    // (NetworkError — refresh cookie may still be valid), or 'fatal' for
    // 4xx auth-loss / abort.
    type AttemptOutcome = 'session' | 'cancelled' | 'throttle' | 'transient' | 'fatal';
    // INVARIANT: track the last fatal error so the eventual clear path can
    // decide whether to broadcast session-lost. A 4xx ApiClientError from
    // refresh is authoritative auth-loss and should propagate to sister
    // tabs. ZodError-after-budget is a backend contract bug, not auth-loss
    // — clearing locally is enough; broadcasting would log other tabs out
    // for no security reason.
    let lastFatalError: unknown = null;
    const attemptBootstrap = async (): Promise<AttemptOutcome> => {
      try {
        const newAccess = await coordinatedRefresh(); // single-flight mutex dedups parallel boot runs
        if (cancelled) return 'cancelled';
        // skipAuthRetry: we JUST minted newAccess; if /auth/me 401s here it's
        // not a session-loss we can recover from via another refresh — e.g.
        // clock skew or user just got deactivated — so fail the bootstrap
        // instead of burning a second rotation.
        const me = await client.get<MeResponse>(
          '/auth/me',
          {
            signal: ac.signal,
            skipAuthRetry: true,
          },
          MeResponseSchema,
        );
        if (cancelled) return 'cancelled';
        // INVARIANT (#440 Phase 3): `MeResponse` extends `userWithPermissions`
        //   with top-level `isOnShift` + `isLastOnShift`. Strip those two
        //   along with `permissions` BEFORE setSession so the stored
        //   `AuthState.user` matches `UserSchema` exactly — no stale shift
        //   fields that a future `setUserShift` would leave behind for any
        //   consumer that spreads/reads the user object.
        const { permissions, isOnShift, isLastOnShift: _isLastOnShift, ...user } = me;
        setSession(newAccess, user, permissions, isOnShift);
        return 'session';
      } catch (e) {
        if (cancelled) return 'cancelled';
        if (e instanceof ThrottleError) return 'throttle';
        // ThrottleError extends NetworkError, so the ThrottleError check must
        // come first. Bare NetworkError = 5xx from refresh / network fault:
        // cookie may still be valid; reschedulable.
        if (e instanceof NetworkError) return 'transient';
        // /auth/me 5xx surfaces as ApiClientError (not NetworkError) because
        // webApiClient parses the error envelope before throwing. Treat
        // those as transient too — the refresh succeeded, the cookie is
        // valid, only /auth/me hit a flake. Otherwise a long-tail success
        // followed by a 503 on /auth/me would clear() a valid session.
        if (e instanceof ApiClientError && e.httpStatus >= 500) return 'transient';
        // ZodError = /auth/me 200 body failed schema parse (backend contract
        // drift). The refresh cookie is still valid; clear()-ing on the first
        // tick would log out a valid session over a server-side bug. Surface
        // as 'transient' (long-tail retry, no clear) up to a small budget —
        // beyond that, fall through to 'fatal' so the user lands on /login
        // instead of an infinite LoadingState. See
        // `CONTRACT_DRIFT_RETRY_BUDGET` above.
        if (e instanceof ZodError) {
          contractDriftCount += 1;
          console.error(
            `[useAuthBootstrap] /auth/me response failed schema parse (attempt ${contractDriftCount}/${CONTRACT_DRIFT_RETRY_BUDGET})`,
            e,
          );
          if (contractDriftCount >= CONTRACT_DRIFT_RETRY_BUDGET) {
            lastFatalError = e;
            return 'fatal';
          }
          return 'transient';
        }
        lastFatalError = e;
        return 'fatal'; // 4xx auth-loss or abort
      }
    };

    const fatalClear = (): void => {
      const err = lastFatalError;
      // 4xx ApiClientError from refresh = authoritative auth-loss → propagate
      // to sister tabs. Anything else (abort, ZodError-after-budget, unknown)
      // = silent local clear.
      if (err instanceof ApiClientError && err.httpStatus >= 400 && err.httpStatus < 500) {
        const reason =
          err.code === 'AUTH_REFRESH_INVALID'
            ? 'AUTH_REFRESH_INVALID'
            : err.code === 'AUTH_REFRESH_REUSED'
              ? 'AUTH_REFRESH_REUSED'
              : 'AUTH_REFRESH_4XX';
        clearWithBroadcast(reason);
      } else {
        clear();
      }
    };

    // Long-tail retry loop. Runs ONLY after the inner 4-attempt budget is
    // exhausted on persistent 429. Sets bootstrapThrottled so ProtectedRoute
    // shows LoadingState (and DOES NOT redirect) while we wait.
    //   'session' → setSession already cleared the flag.
    //   'throttle' → reschedule with the same LONG_TAIL_DELAY_MS (do NOT
    //                fall back to inner-loop 1s/2s/4s, that budget is spent).
    //   'transient' (5xx) → reschedule, preserve session.
    //   'fatal' (4xx) → clear() (already resets bootstrapThrottled).
    const scheduleLongTailRetry = (): void => {
      if (cancelled) return;
      longTailTimerId = setTimeout(() => {
        longTailTimerId = null;
        if (cancelled) return;
        void (async () => {
          const outcome = await attemptBootstrap();
          if (cancelled) return;
          if (outcome === 'session' || outcome === 'cancelled') return;
          if (outcome === 'fatal') {
            fatalClear(); // resets bootstrapThrottled
            return;
          }
          // 'throttle' or 'transient': keep the flag set, try again.
          scheduleLongTailRetry();
        })();
      }, getLongTailDelayMs());
    };

    (async () => {
      let outcome = await attemptBootstrap();
      // Bootstrap-time @Throttle on /auth/refresh would otherwise bounce a
      // valid session to /login (clear() → ProtectedRoute redirect). Retry
      // ThrottleError up to 3x with backoff (1s → 2s → 4s, total ~7s) so
      // a transient throttle window expires inside the bootstrap render.
      // Only 429 is retried by this inner loop; 5xx ('transient') and 4xx
      // ('fatal') fall through to clear() to bound boot latency.
      for (const delay of THROTTLE_RETRY_DELAYS_MS) {
        if (outcome !== 'throttle') break;
        await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        outcome = await attemptBootstrap();
      }
      if (outcome === 'cancelled') return;
      if (outcome === 'session') return; // setSession already cleared hydrating
      if (outcome === 'throttle' || outcome === 'transient') {
        // INVARIANT: do NOT flip hydrating in this branch. The effect deps
        // include hydrating; flipping it here would re-fire the effect,
        // tearing down the long-tail timer via the cleanup. Leave hydrating
        // true alongside bootstrapThrottled — ProtectedRoute already
        // collapses both into LoadingState.
        //
        // 'transient' (5xx from /auth/refresh OR /auth/me httpStatus>=500)
        // routes here too: the refresh cookie may still be valid, so we
        // mustn't clear() on a backend flake. Same long-tail retry as 429.
        setBootstrapThrottled(true);
        scheduleLongTailRetry();
        return;
      }
      // 'fatal' (4xx auth-loss or abort or ZodError-after-budget). The
      // helper picks `clearWithBroadcast` only for 4xx ApiClientError;
      // anything else is a silent local clear so a backend contract bug
      // doesn't log out sister tabs in this profile.
      fatalClear();
    })();

    return () => {
      cancelled = true;
      ac.abort();
      if (longTailTimerId !== null) {
        clearTimeout(longTailTimerId);
        longTailTimerId = null;
      }
    };
  }, [hydrating, client, setSession, clear, setBootstrapThrottled]);
}
